import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from 'antd';
import { CalendarOutlined, CloseCircleFilled } from '@ant-design/icons';
import moment from 'moment';

/**
 * CustomDatePicker - A highly controlled Ant Design DatePicker
 * Supports:
 * 1. Manual typing with calendar sync (via pickerValue)
 * 2. Hover cross icon to clear
 * 3. Strict moment parsing
 */
const CustomDatePicker = ({
    value,
    onChange,
    format = 'DD-MM-YYYY',
    disabled,
    placeholder,
    disabledDate,
    style,
    // Optional overrides for the calendar icon (shown normally) and the clear
    // icon (shown on hover when there's a value) - default to the built-in
    // icons below when not passed, so existing callers are unaffected. The
    // clear click handler is always wired up regardless of which icon is used.
    suffixIcon,
    clearIcon,
    // When true, the hover-clear icon is suppressed if the current value is
    // today's date - for date-filter-style usage where "today" is treated
    // as the default/non-clearable state rather than a real user selection.
    hideClearOnToday = false,
    ...props
}) => {
    // value: Actual selected date
    // pickerValue: Calendar internal view (month/year)
    const defaultYearOffset = moment().subtract(14, 'years');
    const [internalValue, setInternalValue] = useState(value ? moment(value, format) : null);
    const [pickerValue, setPickerValue] = useState(value ? moment(value, format) : defaultYearOffset);
    const [isHovered, setIsHovered] = useState(false);
    const [openKey, setOpenKey] = useState(0);
    const [isOpen, setIsOpen] = useState(false);

    // Display formatting (separate from the DD-MM-YYYY typing mask below).
    // Two distinct concerns, tracked separately:
    //   - isFocused: does the input have real DOM focus right now? Used only
    //     to decide whether external `value` prop updates are safe to apply
    //     (see the sync effect below) - nothing to do with what's displayed.
    //   - isEditingDigits: should the raw DD-MM-YYYY digits be shown (true)
    //     or the "D MMM YYYY" formatted text (false)? This is what actually
    //     drives the display, and it can flip to false WHILE STILL FOCUSED -
    //     the moment typing completes a valid 8-digit date, or a calendar
    //     date is picked, the display switches immediately without needing
    //     blur/clicking away. It's set back to true on focus (so re-editing
    //     starts from raw digits) and, via handleKeyDown below, the instant
    //     the user resumes typing into an already-formatted display without
    //     having blurred first.
    //
    // Both are real state (not just refs) because they drive the format
    // actually passed to the underlying antd <DatePicker> below - a ref
    // change alone wouldn't trigger antd to recompute the input's displayed
    // text. This matters because antd redraws the input from value+format on
    // every one of ITS OWN re-renders, not just when value changes - an
    // earlier version of this file fixed the format string at 'DD-MM-YYYY'
    // permanently and tried to win the display back with a one-off effect,
    // which worked until any *unrelated* parent re-render (common on busy
    // pages) reasserted the DD-MM-YYYY text and stomped it. Deriving the
    // antd-facing format from state on every render, instead of racing to
    // overwrite it after the fact, means any render - for any reason -
    // always shows the correct format.
    const [isFocused, setIsFocused] = useState(false);
    const [isEditingDigits, setIsEditingDigits] = useState(false);
    // internalValueRef/isEditingDigitsRef mirror the state above for the
    // native DOM listeners below, which are attached once per
    // [format, onChange, openKey] change and would otherwise close over
    // stale values.
    const internalValueRef = useRef(internalValue);
    internalValueRef.current = internalValue;
    const isEditingDigitsRef = useRef(isEditingDigits);
    isEditingDigitsRef.current = isEditingDigits;

    // What's actually passed to antd's <DatePicker format=...> prop: the
    // "D MMM YYYY" display format once there's a complete valid date and
    // we're not currently in digit-editing mode, otherwise the component's
    // own DD-MM-YYYY editing format (the format prop this component was
    // configured with).
    const displayFormat = (!isEditingDigits && internalValue && internalValue.isValid())
        ? 'D MMM YYYY'
        : format;

    // Sync external value changes - but never while the field is focused.
    // Callers commonly compute their `value` prop with an "empty means
    // today" fallback (e.g. `currentDate ? moment(currentDate, fmt) :
    // moment()`), which made sense when the only way to clear was a
    // deliberate click on the X button. Now that typing itself clears the
    // field digit-by-digit (0 digits => onChange(null) => parent's
    // `currentDate` goes empty => that ternary immediately resolves to
    // today), the exact same prop round-trip fires mid-edit too - and
    // without this guard it would come straight back down as a new `value`
    // and stomp whatever the user was about to type with today's date. This
    // also covers the more general case: ANY external value change while
    // the user is mid-edit (including this page's periodic auto-refresh)
    // could otherwise flicker the input back to the last-committed value.
    // Skipping sync while focused defers it until blur, where the field is
    // re-synced against whatever `value` is by then.
    useEffect(() => {
        if (isFocused) return;
        if (value) {
            const parsedDate = moment(value, format, true);
            if (parsedDate.isValid()) {
                setInternalValue(parsedDate);
                setPickerValue(parsedDate);
            }
        } else {
            setInternalValue(null);
            // Default picker view to offset when cleared
            setPickerValue(defaultYearOffset);
            if (!isOpen) {
                setOpenKey(prev => prev + 1);
            }
        }
    }, [value, format, isOpen, isFocused]);

    // Update internal calendar panel view
    const handlePanelChange = (newPickerValue) => {
        setPickerValue(newPickerValue);
    };

    const handleOpenChange = (open) => {
        setIsOpen(open);
        if (open && !internalValue) {
            setPickerValue(defaultYearOffset.clone());
        }
        if (props.onOpenChange) {
            props.onOpenChange(open);
        }
    };

    const handleChange = (newDate, dateString) => {
        setInternalValue(newDate);

        if (newDate && newDate.isValid()) {
            setPickerValue(newDate); // Sync calendar UI to typed/selected date
            // A calendar pick is always a completed selection - show it
            // formatted immediately, same as finishing typing does below.
            setIsEditingDigits(false);
            if (onChange) onChange(newDate);
        } else if (!newDate) {
            // This happens when backspacing/clearing manually
            if (onChange) onChange(null);
        }
    };

    const handleClear = (e) => {
        // Stop propagation so the calendar doesn't open when clicking the X
        if (e) e.stopPropagation();
        setInternalValue(null);
        setPickerValue(defaultYearOffset); // Reset calendar view to DOB-friendly year
        setOpenKey(prev => prev + 1);
        if (onChange) onChange(null);
    };

    const containerRef = useRef(null);

    useEffect(() => {
        if (format !== 'DD-MM-YYYY') return;

        const input = containerRef.current?.querySelector('input');
        if (!input) return;

        const maskDate = (digits) => {
            const mask = 'DD-MM-YYYY';
            let result = '';
            let digitIdx = 0;
            for (let i = 0; i < mask.length; i++) {
                if (mask[i] === '-') {
                    result += '-';
                } else {
                    if (digitIdx < digits.length) {
                        result += digits[digitIdx];
                        digitIdx++;
                    } else {
                        result += mask[i];
                    }
                }
            }
            return result;
        };

        const handleInput = (e) => {
            const input = e.target;
            let val = input.value;
            let selectionStart = input.selectionStart;

            // Extract digits only
            const digits = val.replace(/\D/g, '').substring(0, 8);
            
            if (digits.length === 0) {
                // Let it be empty if user cleared everything
                input.value = "";
                setInternalValue(null);
                if (onChange) onChange(null);
                return;
            }

            const maskedValue = maskDate(digits);
            
            // Calculate new cursor position
            // If we added a digit, we might need to skip a dash
            let newPos = selectionStart;
            const isAdding = e.inputType?.includes('insert');
            const isDeleting = e.inputType?.includes('delete');

            if (isAdding) {
                // If the character before the new position in masked value is a dash, 
                // and it wasn't there in the typed sequence, move past it.
                if (maskedValue[selectionStart - 1] === '-' && val[selectionStart - 1] !== '-') {
                    newPos++;
                }
            }

            input.value = maskedValue;
            input.setSelectionRange(newPos, newPos);

            // If we have all 8 digits, validate and sync
            if (digits.length === 8) {
                const parsedDate = moment(maskedValue, format, true);
                if (parsedDate.isValid()) {
                    const current = internalValueRef.current;
                    const isSameDate = current && parsedDate.isSame(current, 'day');

                    setInternalValue(parsedDate);
                    setPickerValue(parsedDate);
                    // Show "D MMM YYYY" immediately - don't wait for blur.
                    // Immediate DOM write for a no-flash transition (same
                    // reasoning as focus/blur below); isEditingDigitsRef is
                    // updated in lockstep so handleKeyDown (which reads it
                    // synchronously) sees this right away too, even before
                    // the setIsEditingDigits(false) render lands.
                    isEditingDigitsRef.current = false;
                    setIsEditingDigits(false);
                    input.value = parsedDate.format('D MMM YYYY');
                    
                    // Only call onChange (and trigger loaders) if the date actually changed
                    if (!isSameDate && onChange) {
                        onChange(parsedDate);
                    }
                }
            }
        };

        const handleKeyDown = (e) => {
            const isNumber = /^[0-9]$/.test(e.key);
            const isControlKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key);

            if (!isNumber && !isControlKey) {
                e.preventDefault();
                return;
            }

            // If we are already editing and have 8 digits, don't allow typing more numbers
            // unless the user has highlighted text to replace.
            if (isEditingDigitsRef.current && isNumber) {
                const digits = input.value.replace(/\D/g, '');
                if (digits.length >= 8 && input.selectionStart === input.selectionEnd) {
                    e.preventDefault();
                    return;
                }
            }

            // If the display currently shows the formatted "D MMM YYYY" text
            // (because typing just completed a date, or a calendar date was
            // picked, without the user blurring in between) and they start
            // editing again, revert to the raw digit form first - mirroring
            // what focusing the field does - so this keystroke lands on
            // digits instead of mangling the formatted text. Only digit/
            // backspace/delete presses trigger this (checked above);
            // navigation keys pass through untouched.
            if (!isEditingDigitsRef.current && (isNumber || e.key === 'Backspace' || e.key === 'Delete')) {
                isEditingDigitsRef.current = true;
                setIsEditingDigits(true);
                const current = internalValueRef.current;
                if (current && current.isValid()) {
                    input.value = current.format('DD-MM-YYYY');
                    // Cursor at the end, nothing selected - see handleFocus below.
                    input.setSelectionRange(input.value.length, input.value.length);
                    // Prevent inserting the number if we already have 8 digits
                    if (isNumber) {
                        e.preventDefault();
                    }
                } else {
                    input.value = '';
                }
            }
        };

        // Focus: switch the display back to the raw DD-MM-YYYY digit form so
        // the mask/backspace logic above keeps working on digits, even if the
        // field currently shows the "D MMM YYYY" formatted display.
        const handleFocus = () => {
            setIsFocused(true);
            isEditingDigitsRef.current = true;
            setIsEditingDigits(true);
            const current = internalValueRef.current;
            if (current && current.isValid()) {
                // Immediate DOM write so there's no flash waiting for the
                // state updates to re-render - the subsequent render (via
                // displayFormat) will show the same text anyway.
                input.value = current.format('DD-MM-YYYY');
                // Place the cursor at the end rather than selecting
                // everything - a click into the field shouldn't highlight
                // the whole date, just be ready to type at the end.
                input.setSelectionRange(input.value.length, input.value.length);
            }
        };

        // Blur: reformat to "D MMM YYYY" only when there's a complete, valid
        // date. If the edit was left incomplete/invalid, internalValue still
        // holds whatever the last valid parsed date was (handleInput only
        // ever updates it once all 8 digits parse cleanly, and never sets a
        // partial value) - so reformatting from it here naturally reverts to
        // the last valid formatted date instead of showing "12 Aug 2026" for
        // a half-typed value. If there's no valid value at all (fully
        // cleared), leave the input as-is.
        const handleBlur = () => {
            setIsFocused(false);
            isEditingDigitsRef.current = false;
            setIsEditingDigits(false);
            const current = internalValueRef.current;
            if (current && current.isValid()) {
                // Immediate DOM write for the same no-flash reason as focus
                // above - displayFormat will independently confirm this same
                // text on the next render, and will keep confirming it on
                // every subsequent render too (unlike the old effect-based
                // approach, which only ran once).
                input.value = current.format('D MMM YYYY');
            }
        };

        input.addEventListener('input', handleInput);
        input.addEventListener('keydown', handleKeyDown);
        input.addEventListener('focus', handleFocus);
        input.addEventListener('blur', handleBlur);

        return () => {
            input.removeEventListener('input', handleInput);
            input.removeEventListener('keydown', handleKeyDown);
            input.removeEventListener('focus', handleFocus);
            input.removeEventListener('blur', handleBlur);
        };
        // openKey is included because handleClear() (and the external-value-sync
        // effect above, when value becomes falsy) bump it specifically to force
        // antd's <DatePicker> to remount with a brand-new <input> DOM node -
        // without openKey here, these listeners would stay attached to the old,
        // now-detached input and the fresh one would have no mask/focus/blur
        // handling at all, making it look like typing/clearing stopped working.
    }, [format, onChange, openKey]);

    // Replace default icon with dynamic hover icon
    const isToday = internalValue && internalValue.isValid() && internalValue.isSame(moment(), 'day');
    const SuffixIcon = () => {
        if (!disabled && isHovered && internalValue && !(hideClearOnToday && isToday)) {
            return clearIcon ? (
                <span
                    onMouseDown={handleClear}
                    style={{ cursor: 'pointer', position: 'relative', zIndex: 10, pointerEvents: 'auto', display: 'inline-flex' }}
                >
                    {clearIcon}
                </span>
            ) : (
                <CloseCircleFilled
                    style={{
                        color: 'rgba(0, 0, 0, 0.45)',
                        cursor: 'pointer',
                        fontSize: 14,
                        position: 'relative',
                        zIndex: 10,
                        pointerEvents: 'auto'
                    }}
                    onMouseDown={handleClear}
                />
            );
        }
        return suffixIcon || <CalendarOutlined style={{ color: 'rgba(0, 0, 15, 0.45)', fontSize: 14 }} />;
    };

    return (
        <div 
            ref={containerRef}
            style={{ width: '100%', position: 'relative' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <DatePicker
                key={openKey}
                {...props}
                disabled={disabled}
                placeholder={placeholder}
                disabledDate={disabledDate}
                style={{ width: '100%', ...style }}
                format={displayFormat}
                value={internalValue}
                pickerValue={pickerValue}
                // Belt-and-braces alongside the `pickerValue` controlled prop above:
                // whenever `key={openKey}` forces antd to remount its internal
                // <DatePicker> (on clear, or the value becoming empty), antd seeds
                // its panel view from THIS prop on that fresh instance's very first
                // paint, before it has reconciled against `pickerValue` - so leaving
                // this fixed at defaultYearOffset ("today minus 14 years", meant only
                // for an empty DOB field - see detailFields.js) made every such
                // remount briefly flash the calendar open ~14 years in the past
                // instead of the actual selected/current date. Prefer the real value
                // here too; defaultYearOffset is now only reached when there truly
                // isn't one.
                defaultPickerValue={internalValue || pickerValue || defaultYearOffset}
                onPanelChange={handlePanelChange}
                onOpenChange={handleOpenChange}
                onChange={handleChange}
                allowClear={false} // Disable default clear for our custom hover logic
                suffixIcon={<SuffixIcon />}
                onOk={() => setPickerValue(internalValue || moment())}
            />
        </div>
    );
};

export default CustomDatePicker;
